// Cookie-based SSO with auth.redwoodoperations.com.
// Reads the rw_token cookie (a Cognito ID token) set by the auth service.
// On 401 from any API call, redirect to auth for login.

const TOKEN_COOKIE = 'rw_token';
const AUTH_URL = 'https://auth.redwoodoperations.com';
const LOOP_GUARD_KEY = 'rw_redirect_attempts';
const MAX_REDIRECT_ATTEMPTS = 2;

export interface RwUser {
  sub: string;
  email: string;
  name: string;
  groups: string[];
  exp: number;
}

export function getToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${TOKEN_COOKIE}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getUser(): RwUser | null {
  const token = getToken();
  if (!token) {
    debugCookieState('no token in document.cookie');
    return null;
  }
  try {
    // Cognito ID tokens are real JWTs: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      debugCookieState('token does not look like a JWT (expected 3 parts)');
      return null;
    }
    const payloadB64 = parts[1];
    const padding = '='.repeat((4 - (payloadB64.length % 4)) % 4);
    const json = atob((payloadB64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;

    const exp = Number(payload.exp ?? 0);
    if (!exp || exp < Date.now() / 1000) {
      debugCookieState('token exp is in the past', payload);
      return null;
    }

    const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];

    // Service permission check: must have at least one wiki:* group
    if (!groups.some((g) => g.startsWith('wiki:'))) {
      debugCookieState('user has no wiki:* group', payload);
      return null;
    }

    // Successful auth — clear loop guard
    try {
      sessionStorage.removeItem(LOOP_GUARD_KEY);
    } catch {
      /* sessionStorage might be unavailable */
    }

    return {
      sub: String(payload.sub ?? ''),
      email: String(payload.email ?? ''),
      name: String(payload.name ?? payload.email ?? ''),
      groups,
      exp,
    };
  } catch (e) {
    debugCookieState('token decode failed', e);
    return null;
  }
}

function debugCookieState(reason: string, extra?: unknown): void {
  if (typeof console === 'undefined') return;
  console.warn('[wiki auth] not authenticated:', reason);
  console.warn(
    '[wiki auth] document.cookie =',
    typeof document !== 'undefined' ? document.cookie : '<no document>',
  );
  if (extra !== undefined) console.warn('[wiki auth] details:', extra);
}

/**
 * Bounce to auth for login. Includes a loop guard: if we get bounced back here
 * without a valid cookie too many times, show an error page instead of looping.
 */
export function redirectToLogin(): never {
  let attempts = 0;
  try {
    attempts = parseInt(sessionStorage.getItem(LOOP_GUARD_KEY) ?? '0', 10) || 0;
  } catch {
    /* ignore */
  }

  if (attempts >= MAX_REDIRECT_ATTEMPTS) {
    showAuthFailedPage();
    throw new Error('redirect loop blocked');
  }

  try {
    sessionStorage.setItem(LOOP_GUARD_KEY, String(attempts + 1));
  } catch {
    /* ignore */
  }

  const here = encodeURIComponent(window.location.href);
  window.location.href = `${AUTH_URL}?redirect=${here}`;
  throw new Error('redirecting');
}

function showAuthFailedPage(): void {
  if (typeof document === 'undefined') return;
  document.body.innerHTML = `
    <div style="font-family:system-ui,sans-serif;max-width:600px;margin:80px auto;padding:0 24px;color:#1a1a1a">
      <h1 style="font-size:28px;color:#8b4513;margin:0 0 16px">Authentication failed</h1>
      <p style="line-height:1.6">
        We bounced you to <a href="${AUTH_URL}" style="color:#8b4513">auth.redwoodoperations.com</a>
        for login, but you came back without a valid session, or your account doesn't have the
        <code>wiki:*</code> permission.
      </p>
      <p style="line-height:1.6"><strong>To fix this:</strong></p>
      <ol style="line-height:1.8">
        <li>Open <a href="${AUTH_URL}" style="color:#8b4513">${AUTH_URL}</a> directly</li>
        <li>Log in fresh</li>
        <li>If you still can't access the wiki, ask an admin to add you to the <code>wiki:viewer</code> group</li>
      </ol>
      <p style="margin-top:32px;color:#888;font-size:13px">
        Debug info (open the browser console for more):<br/>
        document.cookie = <code>${(document.cookie || '(empty)').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]!))}</code>
      </p>
    </div>
  `;
  try {
    sessionStorage.removeItem(LOOP_GUARD_KEY);
  } catch {
    /* ignore */
  }
}

export function signOut(): void {
  document.cookie = `${TOKEN_COOKIE}=; domain=.redwoodoperations.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  try {
    sessionStorage.removeItem(LOOP_GUARD_KEY);
  } catch {
    /* ignore */
  }
  window.location.href = AUTH_URL;
}

/**
 * True if the user has write access to the wiki (wiki:editor or wiki:admin).
 * Use to gate edit affordances in the UI. The backend independently enforces
 * the same check on every write endpoint via requireEditor.
 */
export function canEdit(user: RwUser | null | undefined): boolean {
  if (!user) return false;
  return user.groups.includes('wiki:editor') || user.groups.includes('wiki:admin');
}
